import { MODELS_BY_PROVIDER } from "@/lib/mapper/ai-models";
import type { AIProvider } from "@/lib/mapper/types";

// Provider-level fallback defaults — used only when /api/settings doesn't
// return a configured default model. The actual catalogue lives in
// `@/lib/mapper/ai-models`.
export const FALLBACK_DEFAULT_MODELS: Record<AIProvider, string> = {
  anthropic:
    MODELS_BY_PROVIDER.anthropic.find((m) => m.id === "claude-sonnet-4-6")?.id ??
    MODELS_BY_PROVIDER.anthropic[0]!.id,
  openai:
    MODELS_BY_PROVIDER.openai.find((m) => m.id === "gpt-5-mini")?.id ??
    MODELS_BY_PROVIDER.openai[0]!.id,
  "claude-local":
    MODELS_BY_PROVIDER["claude-local"].find((m) => m.id === "sonnet")?.id ??
    MODELS_BY_PROVIDER["claude-local"][0]!.id,
  openrouter: MODELS_BY_PROVIDER.openrouter[0]!.id,
};

export type DefaultModels = Record<AIProvider, string>;

// ---------------------------------------------------------------------------
// Accessible-models annotation
//
// Settings persists `v2.ai.<provider>.accessibleModels` after each Test
// Connection run in Settings. Shape:
//   { ok: boolean; models: string[]; checkedAt: string; error?: string }
//
// - OpenAI: `models` is the user's filtered list of accessible chat ids.
// - Anthropic / OpenRouter: `models` is [] and `ok: true` means "full catalogue access".
// - Absent (or `ok: false`) → we don't claim anything; render as untested.
// ---------------------------------------------------------------------------

export type AccessibleModelsSetting = {
  ok: boolean;
  models: string[];
  checkedAt: string;
  error?: string;
};

export type AccessibleModelsMap = Partial<Record<AIProvider, AccessibleModelsSetting>>;

export type ModelStatus =
  | "untested"
  | "accessible"
  | "inaccessible"
  | "ok-full-catalogue";

export function statusForModel(
  provider: AIProvider,
  modelId: string,
  accessible: AccessibleModelsMap,
  isCustom: boolean,
): ModelStatus {
  // Custom (off-catalogue) ids can't be predicted.
  if (isCustom) return "untested";
  const entry = accessible[provider];
  // No record yet (or test failed outright) → don't claim anything.
  if (!entry || !entry.ok) return "untested";
  // Anthropic / OpenRouter "full catalogue access" signal.
  if (entry.models.length === 0) return "ok-full-catalogue";
  // OpenAI: explicit per-id allowlist.
  return entry.models.includes(modelId) ? "accessible" : "inaccessible";
}

export function decorateOptionLabel(base: string, status: ModelStatus): string {
  switch (status) {
    case "accessible":
    case "ok-full-catalogue":
      return `✓ ${base}`;
    case "inaccessible":
      return `🔒 ${base}, not in your tier`;
    case "untested":
      return base;
  }
}
